import React, { useEffect, useImperativeHandle, useRef, useCallback } from 'react';

import {
  VideoCameraOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useChunkedVideoRecorder } from '@hooks/operator/useChunkedVideoRecorder';
import type { Subtitle, Magnifier, SessionMeta } from '@hooks/operator/useChunkedVideoRecorder';
import { Button, Space, Typography, Alert } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export interface ChunkedVideoRecorderHandle {
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<SessionMeta | null>;
  stopAndRestartRecording: () => Promise<SessionMeta | null>;
  cancelRecording: () => void;
  readonly isRecording: boolean;
  readonly videoElement: HTMLVideoElement | null;
}

export interface ChunkedVideoRecorderProps {
  orderNumber: string;
  subtitle?: Subtitle | null;
  magnifier?: Magnifier | null;
  autoStart?: boolean;
  placeholderText?: string;
  // Hide the manual pause/resume controls. Use when an external state
  // (e.g. QC's COMPLETED countdown) owns the stop transition and manual
  // pause would create ambiguous state.
  hidePauseControls?: boolean;
  // Slot rendered between the error alert and the controls bar. QC fills it
  // with the auto-stop countdown; return verification leaves it empty.
  children?: React.ReactNode;
  onChunk: (blob: Blob, seq: number, sessionId: string) => void;
  onError?: (err: Error) => void;
  // Fires whenever the underlying MediaRecorder transitions between recording
  // and not-recording. Wrappers that gate behavior on recording state (QC's
  // COMPLETED countdown) need this because ref reads don't drive re-renders.
  onRecordingChange?: (isRecording: boolean) => void;
}

/**
 * 通用 chunked 錄影元件。把 QC 和退貨驗收都用得到的部分抽出來：
 *
 * 1. **掛載即開相機**：`openCamera()` in mount effect 讓 AE/AWB 在實際開始錄影前
 *    先收斂，避免冷啟動偏藍/偏暗的 frame。內部 dedupe，React StrictMode 雙掛載
 *    安全。
 * 2. **autoStart**：prop 為 true 時自動 startRecording。實際停止由 ref 暴露的
 *    `stopRecording` / `cancelRecording` 控制；元件本身不知道任何業務流程
 *    （訂單、出貨、退貨）。
 * 3. **Canvas overlay**：所有 overlay（單號、時間戳、字幕、放大鏡）燒在 canvas 上
 *    並同時餵給 MediaRecorder，預覽和錄影完全一致。
 * 4. **Chunked 上傳的 plumbing**：每段 chunk 都呼叫外部傳進來的 `onChunk` callback；
 *    元件不認識上傳佇列。
 *
 * 設計原則：保持與業務無關。如果 `qcFlowState`、`returnReceiptId` 之類的 prop 開始
 * 出現在這個檔案，就是 wrap 的人該負責的，不要往這裡塞。
 */
const ChunkedVideoRecorder = React.forwardRef<ChunkedVideoRecorderHandle, ChunkedVideoRecorderProps>(({
  orderNumber,
  subtitle = null,
  magnifier = null,
  autoStart = false,
  placeholderText,
  hidePauseControls = false,
  children,
  onChunk,
  onError,
  onRecordingChange,
}, ref) => {
  const { t } = useTranslation();
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    isRecording,
    isCameraReady,
    isPaused,
    recordingTime,
    error,
    videoPreviewRef,
    canvasRef,
    openCamera,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    stopAndRestartRecording,
    cancelRecording,
    formatTime,
  } = useChunkedVideoRecorder({
    orderNumber,
    subtitle,
    magnifier,
    onChunk,
    onError,
  });

  useImperativeHandle(ref, () => ({
    startRecording,
    stopRecording,
    stopAndRestartRecording,
    cancelRecording,
    get isRecording() { return isRecording; },
    get videoElement() { return videoPreviewRef.current; },
  }), [ startRecording, stopRecording, stopAndRestartRecording, cancelRecording, isRecording, videoPreviewRef ]);

  // Camera-on-mount：拿 stream 但不寫檔，先讓 AE/AWB 收斂再 startRecording。
  // openCamera() 內部 dedupe，React StrictMode 重複呼叫不會觸發兩次 getUserMedia。
  useEffect(() => {
    openCamera();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoStart && !isRecording) {
      startRecording();
    }
  }, [ autoStart ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bubble recording state to wrappers that need to react to it without taking
  // an imperative ref dependency (e.g. QC's COMPLETED-state countdown trigger).
  useEffect(() => {
    onRecordingChange?.(isRecording);
  }, [ isRecording, onRecordingChange ]);

  const mountCanvas = useCallback(() => {
    if (canvasRef.current && canvasContainerRef.current) {
      const container = canvasContainerRef.current;
      const canvas = canvasRef.current;
      if (canvas.parentNode !== container) {
        container.innerHTML = '';
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.objectFit = 'contain';
        container.appendChild(canvas);
      }
    }
  }, [ canvasRef ]);

  useEffect(() => {
    if (isCameraReady) {
      const timer = setTimeout(mountCanvas, 100);
      return () => clearTimeout(timer);
    }
  }, [ isCameraReady, mountCanvas ]);

  const defaultPlaceholder = placeholderText || t('shared.video_recorder.placeholder', { defaultValue: '準備錄影' });

  return (
    <div className="video-recorder">
      {error && (
        <Alert type="error" message={error} closable onClose={() => {}} style={{ marginBottom: '12px' }} />
      )}

      {children}

      {isRecording && (
        <div className="video-recorder__controls-top">
          <div className="video-recorder__time">
            <Text strong style={{ fontSize: '18px', fontFamily: 'monospace', color: !isPaused ? '#ff4d4f' : '#595959' }}>
              {formatTime(recordingTime)}
            </Text>
          </div>
          <div className="video-recorder__indicator">
            <span className={`video-recorder__indicator-dot ${isPaused ? 'paused' : 'recording'}`} />
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {isPaused
                ? t('shared.video_recorder.paused', { defaultValue: '已暫停' })
                : t('shared.video_recorder.recording', { defaultValue: '錄影中' })}
            </Text>
          </div>
          {!hidePauseControls && (
            <Space>
              {!isPaused ? (
                <Button size="large" icon={<PauseCircleOutlined />} onClick={pauseRecording}>
                  {t('shared.video_recorder.pause', { defaultValue: '暫停' })}
                </Button>
              ) : (
                <Button type="primary" size="large" icon={<PlayCircleOutlined />} onClick={resumeRecording}>
                  {t('shared.video_recorder.resume', { defaultValue: '繼續' })}
                </Button>
              )}
            </Space>
          )}
        </div>
      )}

      <video ref={videoPreviewRef} autoPlay playsInline muted style={{ display: 'none' }} />

      <div className="video-recorder__display">
        <div className="video-recorder__video-container">
          {isCameraReady ? (
            <div
              ref={(el) => { canvasContainerRef.current = el; mountCanvas(); }}
              className="video-recorder__preview"
              style={{ width: '100%', aspectRatio: '16 / 10' }}
            />
          ) : (
            <div className="video-recorder__placeholder">
              <VideoCameraOutlined style={{ fontSize: '48px', color: '#bfbfbf' }} />
              <Text type="secondary">{defaultPlaceholder}</Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

ChunkedVideoRecorder.displayName = 'ChunkedVideoRecorder';

export default ChunkedVideoRecorder;
